// Cue times follow beak_v15.py and detail_v10.py, not dialogue typing.
import type { RamonAction, RamonStage } from "./types";

export function chirpCues(stage: RamonStage, action: RamonAction): number[] {
  if (stage === "egg") return action === "confirm" ? [0.22] : [];
  if (stage !== "chick") return [];
  const cues: Partial<Record<RamonAction, number[]>> = {
    hello: [0.55], hello_short: [0.3], acknowledge: [0],
    celebrate: [0.3], point_left: [0.35], point_right: [0.35], welcome_back: [3.18, 4.02],
  };
  return cues[action] ?? [];
}

let context: AudioContext | undefined;
type Sound = "chirp" | "egg-crack" | "egg-open";
const buffers: Partial<Record<Sound, AudioBuffer>> = {};
const loading: Partial<Record<Sound, Promise<void>>> = {};

function unlock() {
  context ??= new AudioContext();
  void context.resume().catch(() => {});
  for (const sound of ["chirp", "egg-crack", "egg-open"] as const) {
    loading[sound] ??= fetch(`/ramon/audio/${sound}.wav`)
      .then(response => { if (!response.ok) throw new Error("Sound unavailable"); return response.arrayBuffer(); })
      .then(bytes => context!.decodeAudioData(bytes))
      .then(decoded => { buffers[sound] = decoded; })
      .catch(() => { delete loading[sound]; });
  }
}

// Called only by a real user gesture; no autoplay workaround.
export function registerChirpGesture() {
  const gesture = () => { try { unlock(); } catch { /* Audio is optional. */ } };
  window.addEventListener("pointerdown", gesture, { passive: true });
  window.addEventListener("keydown", gesture);
  return () => {
    window.removeEventListener("pointerdown", gesture);
    window.removeEventListener("keydown", gesture);
  };
}

export function playChirp(sound: Sound = "chirp", offset = 0): (() => void) | undefined {
  const buffer = buffers[sound];
  if (!context || context.state !== "running" || !buffer || document.hidden) return;
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(context.destination);
  source.onended = () => source.disconnect();
  source.start(0, offset);
  return () => { try { source.stop(); } catch { /* Already finished. */ } };
}
